/**
 * 기업 정보 / 보안 정책
 * ---------------------------------------------------------------------------
 * 보안 정책 변경은 감사로그 대상이라 서버가 사유를 요구한다.
 * 특히 2차 인증을 끄는 것은 보안 수준을 낮추는 행위라 화면에서 경고를 띄운다.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText, qk } from '../../lib/queries';
import { TenantProfileForm } from './TenantProfileForm';
import { TenantLogoPanel } from './TenantLogoPanel';
import { TerminationPanel } from './TerminationPanel';
import { hasPermission, useAuth } from '../../lib/auth-store';
import SupportAccessPanel from './SupportAccessPanel';
import AiReportPanel from './AiReportPanel';

const { Text, Paragraph } = Typography;

interface TenantProfile {
  tenantId: string;
  slug: string;
  companyName: string;
  bizRegNo: string | null;
  corpRegNo: string | null;
  bizType: string | null;
  bizItem: string | null;
  zipCode: string | null;
  address1: string | null;
  address2: string | null;
  companyTel: string | null;
  companyFax: string | null;
  establishedOn: string | null;
  homepageUrl: string | null;
  ceoName: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  status: string;
  memberCount: number;
}

interface TenantPolicy {
  password: {
    minLength: number;
    requireNumber: boolean;
    requireSpecial: boolean;
    requireMixedCase: boolean;
    historyCount: number;
    expireDays: number | null;
    blockBreached: boolean;
  };
  mfa: { mode: string; trustedDeviceDays: number; forceForAdmin: boolean };
  session: {
    policy: string;
    maxConcurrent: number;
    idleTimeoutMinutes: number;
    absoluteHours: number;
  };
  login: { maxFailures: number; lockoutMinutes: number; ipAllowlist: string[] | null };
  nameSearch: { enabled: boolean; minLength: number; chosung: boolean };
  support: { accessAllowed: boolean; notifyAdmin: boolean };
}

const MFA_OPTIONS = [
  { value: 'OFF', label: 'OFF — 사용 안 함' },
  { value: 'EMAIL', label: 'EMAIL — 이메일 인증코드' },
  { value: 'TOTP', label: 'TOTP — 인증 앱' },
  { value: 'EITHER', label: 'EITHER — 둘 중 하나 (앱 우선)' },
  { value: 'BOTH', label: 'BOTH — 둘 다 요구' },
];

const SESSION_OPTIONS = [
  { value: 'SINGLE_EVICT', label: '1개만 허용 — 새 로그인 시 기존 세션 종료' },
  { value: 'SINGLE_REJECT', label: '1개만 허용 — 기존 세션이 있으면 신규 거부' },
  { value: 'EVICT_OLDEST', label: '다중 허용 — 한도 초과 시 가장 오래된 세션 종료' },
  { value: 'ALLOW_MULTI', label: '다중 허용 — 한도까지' },
];

export default function TenantSettingsPage() {
  const { profile } = useAuth();
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const [policyForm] = Form.useForm();

  const canManage = hasPermission(profile, 'platform.tenant:manage');
  const [saving, setSaving] = useState(false);

  const tenant = useQuery<TenantProfile, NormalizedError>({
    queryKey: qk.tenant,
    queryFn: async () => (await apiClient.get<TenantProfile>('/tenant')).data,
  });

  const policy = useQuery<TenantPolicy, NormalizedError>({
    queryKey: qk.tenantPolicy,
    queryFn: async () => (await apiClient.get<TenantPolicy>('/tenant/policy')).data,
  });

  useEffect(() => {
    if (policy.data) policyForm.setFieldsValue(flatten(policy.data));
  }, [policy.data, policyForm]);

  const savePolicy = async () => {
    const values = await policyForm.validateFields().catch(() => null);
    if (!values) return;

    const before = policy.data;
    const turningOffMfa = before && before.mfa.mode !== 'OFF' && values.mfa_mode === 'OFF';

    const proceed = async (reason: string) => {
      setSaving(true);
      try {
        await apiClient.put('/tenant/policy', { reason, policy: values });
        message.success('보안 정책을 저장했습니다.');
        await client.invalidateQueries({ queryKey: qk.tenantPolicy });
      } catch (err) {
        message.error(errorText(err as NormalizedError));
      } finally {
        setSaving(false);
      }
    };

    let reason = '';
    Modal.confirm({
      title: '보안 정책 변경',
      width: 520,
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          {turningOffMfa && (
            <Alert
              type="warning"
              showIcon
              message="2차 인증을 끄려고 합니다"
              description="비밀번호만으로 로그인할 수 있게 됩니다. 비밀번호가 유출되면 계정이 바로 탈취됩니다."
            />
          )}
          <Text type="secondary">
            변경 사유는 감사로그에 기록됩니다. 나중에 왜 바꿨는지 추적할 수 있도록 구체적으로
            적어 주세요.
          </Text>
          <Input.TextArea
            rows={2}
            placeholder="예: SMTP 미연동으로 임시 비활성화 (2026-10 연동 후 복구 예정)"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </Space>
      ),
      okText: '저장',
      cancelText: '취소',
      async onOk() {
        if (!reason.trim()) {
          message.error('변경 사유를 입력해 주세요.');
          return Promise.reject(new Error('reason required'));
        }
        await proceed(reason.trim());
      },
    });
  };

  const t = tenant.data;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {tenant.isError && <Alert type="error" showIcon message={errorText(tenant.error)} />}

      <Tabs
        items={[
          {
            key: 'profile',
            label: '기업 정보',
            /*
              예전에는 읽기만 되었다. 상호가 바뀌거나 담당자가 바뀌면 고칠
              방법이 없어 운영자에게 연락해야 했다 — 고객이 자기 회사 정보를
              못 고치는 화면이었다. 운영자 화면(기업 관리 상세)과 **같은 폼**을
              쓴다. 주소만 다르다.
            */
            children: (
              <Card loading={tenant.isLoading}>
                <Descriptions column={3} bordered size="small" style={{ marginBottom: 20 }}>
                  <Descriptions.Item label="식별자(slug)">
                    <Tag>{t?.slug}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="상태">
                    <Tag color={t?.status === 'ACTIVE' ? 'green' : 'orange'}>{t?.status}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="구성원 수">{t?.memberCount}명</Descriptions.Item>
                </Descriptions>

                <TenantLogoPanel canManage={canManage} />

                <TenantProfileForm
                  profilePath="/tenant"
                  docsPath="/tenant/documents"
                  cacheKey="self"
                />

                <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0, fontSize: 12 }}>
                  대표자명·담당자 정보는 개인정보이므로 암호화되어 저장됩니다.
                  상호·주소 등 법인 정보는 검색·정렬을 위해 평문으로 보관합니다.
                  식별자와 상태는 바꿀 수 없습니다 — 식별자는 접속 주소에 쓰이고,
                  상태는 운영자가 정합니다.
                </Paragraph>
              </Card>
            ),
          },
          {
            key: 'ai',
            label: 'AI 보고서',
            /*
              설정 정의(config.setting_definition)에 넣어 두면 화면이 알아서
              그려 줄 거라 보았는데, 이 화면은 고정 탭이라 그 표를 읽지 않는다.
              그래서 탭으로 올린다.
            */
            children: <AiReportPanel canManage={canManage} />,
          },
          {
            key: 'policy',
            label: '보안 정책',
            // 탭을 열기 전에도 <Form> 을 렌더해 둔다.
            // 그러지 않으면 정책 데이터가 먼저 도착했을 때 form 인스턴스에
            // 값을 넣을 대상이 없어 antd 경고가 난다.
            forceRender: true,
            children: (
              <Card loading={policy.isLoading}>
                <Form form={policyForm} layout="vertical" disabled={!canManage}>
                  <SectionTitle>2차 인증</SectionTitle>
                  <Space size="middle" wrap style={{ display: 'flex' }}>
                    <Form.Item name="mfa_mode" label="인증 방식" style={{ minWidth: 300 }}>
                      <Select options={MFA_OPTIONS} />
                    </Form.Item>
                    <Form.Item
                      name="mfa_trusted_device_days"
                      label="신뢰 기기 유지(일)"
                      extra="0이면 사용 안 함"
                    >
                      <InputNumber min={0} max={90} />
                    </Form.Item>
                    {/*
                      이 스위치가 인증 방식보다 **세다.** 방식을 OFF 로 두어도
                      켜져 있으면 관리자는 계속 2차 인증을 요구받는다
                      (MfaService.resolveRequirement 가 EITHER 로 올린다).
                      일부러 둔 안전장치인데, 화면에 적혀 있지 않아 "OFF 로
                      바꿨는데 왜 코드를 묻느냐" 가 된다. 적어 둔다.
                    */}
                    <Form.Item
                      name="mfa_force_for_admin"
                      label="관리자 강제"
                      valuePropName="checked"
                      extra="켜 두면 위의 인증 방식이 OFF 여도 관리자에게는 2차 인증을 요구합니다"
                    >
                      <Switch />
                    </Form.Item>
                  </Space>

                  <SectionTitle>비밀번호</SectionTitle>
                  <Space size="middle" wrap style={{ display: 'flex' }}>
                    <Form.Item name="pwd_min_length" label="최소 길이">
                      <InputNumber min={10} max={64} />
                    </Form.Item>
                    <Form.Item name="pwd_history_count" label="재사용 금지 개수">
                      <InputNumber min={0} max={20} />
                    </Form.Item>
                    <Form.Item name="pwd_require_number" label="숫자 필수" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item
                      name="pwd_require_special"
                      label="특수문자 필수"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                    <Form.Item
                      name="pwd_require_mixed_case"
                      label="대소문자 혼용"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Space>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="외부 유출 이력이 있는 비밀번호 차단은 항상 켜져 있으며 끌 수 없습니다."
                  />

                  <SectionTitle>세션 / 동시 접속</SectionTitle>
                  <Space size="middle" wrap style={{ display: 'flex' }}>
                    <Form.Item name="session_policy" label="동시 접속 정책" style={{ minWidth: 340 }}>
                      <Select options={SESSION_OPTIONS} />
                    </Form.Item>
                    <Form.Item name="session_max_concurrent" label="최대 동시 세션">
                      <InputNumber min={1} max={20} />
                    </Form.Item>
                    <Form.Item name="session_idle_timeout_min" label="무활동 만료(분)">
                      <InputNumber min={5} max={480} />
                    </Form.Item>
                    <Form.Item name="session_absolute_hours" label="절대 만료(시간)">
                      <InputNumber min={1} max={168} />
                    </Form.Item>
                  </Space>

                  <SectionTitle>로그인 보호</SectionTitle>
                  <Space size="middle" wrap style={{ display: 'flex' }}>
                    <Form.Item name="login_max_failures" label="잠금 전 실패 허용">
                      <InputNumber min={3} max={20} />
                    </Form.Item>
                    <Form.Item name="login_lockout_minutes" label="잠금 시간(분)">
                      <InputNumber min={1} max={1440} />
                    </Form.Item>
                  </Space>

                  <SectionTitle>이름 검색</SectionTitle>
                  <Space size="middle" wrap style={{ display: 'flex' }}>
                    <Form.Item
                      name="name_search_enabled"
                      label="부분검색 사용"
                      valuePropName="checked"
                      extra="끄면 이름 검색이 비활성화됩니다"
                    >
                      <Switch />
                    </Form.Item>
                    <Form.Item
                      name="name_search_chosung"
                      label="초성 검색"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                    <Form.Item name="name_search_min_length" label="최소 검색어 길이">
                      <InputNumber min={1} max={5} />
                    </Form.Item>
                  </Space>
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="이름 부분검색은 색인이 필요합니다"
                    description="색인은 성씨 빈도 분석에 취약할 수 있습니다. 보안을 우선한다면 끄는 것을 검토하세요."
                  />

                  <SectionTitle>플랫폼 운영자 지원</SectionTitle>
                  <Space size="middle" wrap style={{ display: 'flex' }}>
                    <Form.Item
                      name="support_access_allowed"
                      label="지원 모드 접근 허용"
                      valuePropName="checked"
                      extra="끄면 운영자 권한으로도 접근할 수 없습니다"
                    >
                      <Switch />
                    </Form.Item>
                    <Form.Item
                      name="support_notify_admin"
                      label="접근 시 관리자 알림"
                      valuePropName="checked"
                      extra="메일 연동 후 동작합니다"
                    >
                      <Switch />
                    </Form.Item>
                  </Space>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="지원 모드 접근을 허용해도 기록은 남습니다"
                    description="운영자는 사유를 입력해야 접근할 수 있고, 시간이 지나면 자동으로 끊깁니다. 실제 접근 내역은 [지원 접근 이력] 탭에서 확인하세요."
                  />

                  <Button type="primary" onClick={savePolicy} loading={saving} disabled={!canManage}>
                    보안 정책 저장
                  </Button>
                  {!canManage && (
                    <Text type="secondary" style={{ marginLeft: 12 }}>
                      기업 관리 권한이 필요합니다.
                    </Text>
                  )}
                </Form>
              </Card>
            ),
          },
          {
            key: 'support',
            label: '지원 접근 이력',
            children: <SupportAccessPanel accessAllowed={policy.data?.support.accessAllowed} />,
          },
          {
            key: 'termination',
            /*
              빨간 글씨로 둔다. 다른 탭과 같은 색이면 실수로 눌러 들어간다 —
              들어가서 바로 끝나지는 않지만, 되돌리기 어려운 일로 가는
              문이라는 것이 보여야 한다.
            */
            label: <span style={{ color: '#cf1322' }}>서비스 종료</span>,
            children: <TerminationPanel />,
          },
        ]}
      />
    </Space>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 12 }}>
      {children}
    </Typography.Title>
  );
}

/** 서버의 중첩 응답을 폼이 쓰는 평면 키로 바꾼다. 저장 시 그대로 서버에 보낸다. */
function flatten(policy: TenantPolicy): Record<string, unknown> {
  return {
    pwd_min_length: policy.password.minLength,
    pwd_require_number: policy.password.requireNumber,
    pwd_require_special: policy.password.requireSpecial,
    pwd_require_mixed_case: policy.password.requireMixedCase,
    pwd_history_count: policy.password.historyCount,
    pwd_expire_days: policy.password.expireDays,
    mfa_mode: policy.mfa.mode,
    mfa_trusted_device_days: policy.mfa.trustedDeviceDays,
    mfa_force_for_admin: policy.mfa.forceForAdmin,
    session_policy: policy.session.policy,
    session_max_concurrent: policy.session.maxConcurrent,
    session_idle_timeout_min: policy.session.idleTimeoutMinutes,
    session_absolute_hours: policy.session.absoluteHours,
    login_max_failures: policy.login.maxFailures,
    login_lockout_minutes: policy.login.lockoutMinutes,
    name_search_enabled: policy.nameSearch.enabled,
    name_search_min_length: policy.nameSearch.minLength,
    name_search_chosung: policy.nameSearch.chosung,
    support_access_allowed: policy.support.accessAllowed,
    support_notify_admin: policy.support.notifyAdmin,
  };
}
